import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { refresh as apiRefresh, logout as apiLogout, type UserView } from './api';

type Status = 'unknown' | 'signed-in' | 'signed-out';

type AuthState = {
  status: Status;
  user: UserView | null;
  accessToken: string | null;
  /** Force a refresh against the bc_refresh cookie (called on mount + retry). */
  hydrate: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  status: 'unknown',
  user: null,
  accessToken: null,
  hydrate: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ComponentChildren }) {
  const [status, setStatus] = useState<Status>('unknown');
  const [user, setUser] = useState<UserView | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  async function hydrate() {
    try {
      const res = await apiRefresh();
      setUser(res.user);
      setAccessToken(res.accessToken);
      setStatus('signed-in');
    } catch {
      setUser(null);
      setAccessToken(null);
      setStatus('signed-out');
    }
  }

  async function signOut() {
    try {
      await apiLogout();
    } catch {}
    setUser(null);
    setAccessToken(null);
    setStatus('signed-out');
  }

  useEffect(() => {
    hydrate();
    // Refresh the access token a few minutes before the 15-min TTL runs out.
    // Cheap insurance — if a long-open admin tab is mid-form, we don't want
    // a 401 mid-submit.
    const id = setInterval(hydrate, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, accessToken, hydrate, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
